"use client"

import Link from 'next/link'
import { useParams, usePathname } from 'next/navigation'
import useSWR from 'swr'
import SvgIcon from '@components/shared/svg-icons/SvgIcon'
import { badgeText, unreadLabel, useUnread } from '@components/messages/unread'
import useRedirectBack from '@hooks/useRedirectBack'
import storage from '@utils/localStorage'
import checkLogin from '@utils/checkLogin'
import { isAccountPath } from '@components/account/shell/AccountShell'
import { ADD_OFFER_PAGE, LOGIN_PAGE, MESSAGES_PAGE, USER_ACCOUNT, USER_WATCHED } from '@constants/routes'

import styles from './BottomNav.module.css'

const HOST = process.env.NEXT_PUBLIC_HOST
const SEARCH_FIELD = '#tresc form input:not([type="hidden"])'

const isCurrent = {
    search: (path) => path === '/',
    watched: (path) => path.startsWith(USER_WATCHED),
    add: (path) => path.startsWith('/dodaj-ogloszenie'),
    messages: (path) => path === MESSAGES_PAGE || path.startsWith(MESSAGES_PAGE + '/'),
    account: (path) => (path.startsWith('/profil/') && !path.startsWith(USER_WATCHED)) || path.startsWith('/konto/'),
}

function Item ({ current, href, icon, label, onClick }) {
    return (
        <li>
            <Link aria-current={current ? 'page' : undefined} className={styles.item} href={href} onClick={onClick} prefetch={false}>
                {icon}
                {label}
            </Link>
        </li>
    )
}

// Na stronie głównej „Szukaj” przenosi fokus do wyszukiwarki zamiast przeładowania.
function focusSearch (e) {
    const field = [...document.querySelectorAll(SEARCH_FIELD)].find(el => el.getClientRects().length > 0)
    if (!field) return
    e.preventDefault()
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    window.scrollTo({ top: 0, behavior: reduce ? 'auto' : 'smooth' })
    field.focus({ preventScroll: true })
}

// Poniżej 1024 px; na stronie ogłoszenia jej miejsce zajmuje pasek kontaktu, na stronach konta jej nie ma.
export default function BottomNav () {
    const path = usePathname() || ''
    const params = useParams()
    const { data: user } = useSWR('user', storage)
    const isLoggedIn = checkLogin(user)
    const { total: unread } = useUnread()
    const [, setRedirect] = useRedirectBack()

    if (params?.shortid || isAccountPath(path)) return null

    const onHome = isCurrent.search(path)
    const chatIcon = <SvgIcon name="chat" />

    return (
        <div className={styles.space}>
            <nav aria-label="Nawigacja dolna" className={styles.nav}>
                <ul>
                    <Item current={onHome} href={HOST} icon={<SvgIcon name="search" />} label="Szukaj" onClick={onHome ? focusSearch : undefined} />
                    <Item current={isCurrent.watched(path)} href={HOST + USER_WATCHED} icon={<SvgIcon name="heart" />} label="Obserwowane" />
                    <Item
                        current={isCurrent.add(path)}
                        href={HOST + ADD_OFFER_PAGE}
                        icon={<span className={styles.plus}><SvgIcon name="plus" /></span>}
                        label="Dodaj"
                    />
                    <li>
                        <Link
                            aria-current={isCurrent.messages(path) ? 'page' : undefined}
                            aria-label={isLoggedIn && unread ? unreadLabel(unread) : undefined}
                            className={styles.item}
                            href={HOST + (isLoggedIn ? MESSAGES_PAGE : LOGIN_PAGE)}
                            onClick={isLoggedIn ? undefined : () => setRedirect()}
                            prefetch={false}
                        >
                            <span className={styles.iconWrap}>
                                {chatIcon}
                                { isLoggedIn && Boolean(unread) && <span aria-hidden="true" className={styles.count}>{badgeText(unread)}</span> }
                            </span>
                            Wiadomości
                        </Link>
                    </li>
                    <Item current={isCurrent.account(path)} href={HOST + (isLoggedIn ? USER_ACCOUNT : LOGIN_PAGE)} icon={<SvgIcon name="user" />} label="Konto" />
                </ul>
            </nav>
        </div>
    )
}
